import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  ChevronRight,
  Clock3,
  Crosshair,
  Fingerprint,
  Mail,
  MapPin,
  Menu,
  Phone,
  Radar,
  RotateCcw,
  Search,
  ShieldCheck,
  UserRoundSearch,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "@/components/ui/sonner";

const CONTACT = {
  address: "Via Papa Pio X, 21, 37045 Legnago VR",
  phone: "0442 603886 - 377 1677246",
  email: "info@securcity.it",
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_TITLE = "Città Sicura | Agenzia investigativa di Legnago VR";

const navItems = [
  { label: "Metodo", href: "/#metodo" },
  { label: "Servizi", href: "/#servizi" },
  { label: "Agenzia", href: "/#agenzia" },
  { label: "Contatti", href: "/#contatti" },
];

const legalPages = {
  privacy: {
    eyebrow: "Informativa privacy",
    title: "Privacy Policy",
    subtitle:
      "Informativa essenziale sul trattamento dei dati inviati tramite il modulo di contatto del sito Città Sicura.",
    updated: "Ultimo aggiornamento: Aprile 2026",
    sections: [
      {
        title: "1. Titolare del trattamento",
        body: [
          "Il titolare del trattamento è Città Sicura, con sede in Via Papa Pio X, 21, 37045 Legnago VR. Per richieste relative alla privacy è possibile scrivere a info@securcity.it.",
        ],
      },
      {
        title: "2. Dati raccolti tramite il modulo",
        body: [
          "Attraverso il modulo di contatto vengono richiesti esclusivamente i dati necessari a ricontattare l’utente: nome, cognome, recapito telefonico, indirizzo email e testo della richiesta.",
          "Il sito non raccoglie dati tramite il modulo per finalità di marketing automatico, profilazione o newsletter.",
        ],
      },
      {
        title: "3. Finalità del trattamento",
        body: [
          "I dati sono trattati solo per ricevere la richiesta inviata dal modulo, rispondere all’utente e gestire l’eventuale primo contatto informativo o consulenziale.",
          "La base giuridica è l’esecuzione di misure precontrattuali richieste dall’interessato e il consenso espresso tramite il flag privacy presente nel modulo.",
        ],
      },
      {
        title: "4. Conservazione dei dati",
        body: [
          "I dati inviati tramite il modulo non vengono salvati in un database del sito. Vengono utilizzati per generare e inviare l’email alla casella di contatto dell’agenzia.",
          "La conservazione è limitata al tempo necessario alla gestione della richiesta e all’eventuale scambio email conseguente. In assenza di ulteriori rapporti, i dati vengono mantenuti solo per esigenze organizzative o obblighi di legge eventualmente applicabili.",
        ],
      },
      {
        title: "5. Comunicazione a terzi",
        body: [
          "I dati non sono ceduti a terzi né diffusi. Possono essere trattati da fornitori tecnici strettamente necessari al funzionamento del sito e del servizio email.",
        ],
      },
      {
        title: "6. Diritti dell’interessato",
        body: [
          "L’utente può richiedere accesso, rettifica, cancellazione, limitazione o opposizione al trattamento scrivendo a info@securcity.it.",
        ],
      },
    ],
  },
  cookies: {
    eyebrow: "Informativa cookie",
    title: "Cookie Policy",
    subtitle:
      "Informativa sull’uso di cookie e tecnologie simili nel sito Città Sicura.",
    updated: "Ultimo aggiornamento: Aprile 2026",
    sections: [
      {
        title: "1. Cosa sono i cookie",
        body: [
          "I cookie sono piccoli file di testo che un sito può salvare sul dispositivo dell’utente per consentire funzionalità tecniche, migliorare l’esperienza di navigazione o abilitare servizi esterni.",
        ],
      },
      {
        title: "2. Cookie utilizzati dal sito",
        body: [
          "Il sito Città Sicura non utilizza cookie di profilazione propri e non installa strumenti di marketing o tracciamento pubblicitario tramite il modulo di contatto.",
          "Possono essere presenti cookie tecnici o funzionali necessari alla normale navigazione e al corretto funzionamento delle pagine.",
        ],
      },
      {
        title: "3. Servizi di terze parti",
        body: [
          "Il sito integra una mappa Google Maps per mostrare la sede dell’attività. L’interazione o il caricamento della mappa può comportare il trattamento di dati tecnici da parte di Google secondo le rispettive informative.",
          "Alcune risorse grafiche o font possono essere caricate da provider esterni; tali servizi possono ricevere informazioni tecniche come indirizzo IP e dati del browser per erogare correttamente il contenuto.",
        ],
      },
      {
        title: "4. Gestione dei cookie",
        body: [
          "L’utente può gestire, limitare o cancellare i cookie direttamente dalle impostazioni del proprio browser.",
          "La disattivazione di cookie tecnici o di funzionalità di terze parti può incidere sulla corretta visualizzazione di alcune sezioni, come la mappa di localizzazione.",
        ],
      },
      {
        title: "5. Aggiornamenti",
        body: [
          "La presente Cookie Policy può essere aggiornata in caso di modifiche tecniche al sito o integrazione di nuovi servizi.",
        ],
      },
    ],
  },
};

const services = [
  {
    icon: UserRoundSearch,
    title: "Indagini private",
    description:
      "Supporto discreto per situazioni familiari, personali e patrimoniali, con raccolta di elementi utili e documentabili.",
    features: ["Infedeltà coniugale", "Ricerca persone", "Pre e post matrimoniali", "Tutela minori"],
  },
  {
    icon: BriefcaseBusiness,
    title: "Investigazioni aziendali",
    description:
      "Analisi e verifiche per proteggere patrimonio, reputazione, personale e know-how dell’impresa.",
    features: ["Concorrenza sleale", "Infedeltà dipendenti", "Assenteismo", "Controspionaggio"],
  },
  {
    icon: Fingerprint,
    title: "Indagini scientifiche",
    description:
      "Attività tecniche e consulenze specialistiche a supporto di controversie, sicurezza e accertamenti mirati.",
    features: ["Analisi forense", "Ricerca DNA", "Bonifiche ambientali", "Accertamenti patrimoniali"],
  },
  {
    icon: ShieldCheck,
    title: "Sicurezza e videosorveglianza",
    description:
      "Progettazione di soluzioni di controllo, telecontrollo e prevenzione per abitazioni, attività e aziende.",
    features: ["Videosorveglianza", "Allarmi", "Telecontrollo", "Valutazione rischi"],
  },
];

const stats = [
  { value: "40+", label: "anni di esperienza" },
  { value: "24/7", label: "centrale operativa" },
  { value: "100%", label: "riservatezza" },
];

const captchaChallenges = [
  { question: "Quanto fa 7 + 4?", answer: "11" },
  { question: "Scrivi in cifre: dodici", answer: "12" },
  { question: "Quanto fa 18 - 6?", answer: "12" },
  { question: "Completa: Città ____", answer: "sicura" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0A0D14]/70 backdrop-blur-2xl"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <a href="/" data-testid="header-logo-link" className="group flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full border border-[#C49B5B]/30 bg-[#C49B5B]/10 text-[#C49B5B] transition-transform duration-300 group-hover:scale-105">
            <Radar className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <span className="leading-none">
            <span data-testid="brand-name" className="block font-heading text-lg font-semibold tracking-[0.18em] text-white">
              CITTÀ SICURA
            </span>
            <span data-testid="brand-tagline" className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
              Investigazioni Private
            </span>
          </span>
        </a>

        <nav data-testid="desktop-navigation" className="hidden items-center gap-8 lg:flex">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`desktop-nav-${item.label.toLowerCase()}`}
              className="text-sm font-medium text-zinc-400 transition-colors duration-300 hover:text-[#C49B5B]"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <Button
          asChild
          data-testid="header-contact-button"
          className="hidden rounded-full bg-[#C49B5B] px-6 text-[#0A0D14] shadow-[0_0_30px_rgba(196,155,91,0.25)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#D8B475] hover:shadow-[0_0_45px_rgba(196,155,91,0.35)] lg:inline-flex"
        >
          <a href="/#contatti">Contattaci</a>
        </Button>

        <Button
          type="button"
          variant="ghost"
          data-testid="mobile-menu-button"
          aria-label="Apri menu di navigazione"
          onClick={() => setOpen((value) => !value)}
          className="rounded-full border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {open && (
        <div data-testid="mobile-navigation-panel" className="border-t border-white/5 bg-[#0A0D14]/95 px-5 py-5 backdrop-blur-xl lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-3">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className="rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-3 text-sm font-medium text-zinc-300 transition-colors hover:border-[#C49B5B]/30 hover:text-[#C49B5B]"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 140]);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {
      // Il poster resta visibile se il browser decide di bloccare l'autoplay.
    });
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#0A0D14] pt-20">
      <motion.video
        ref={videoRef}
        style={{ y }}
        data-testid="hero-background-video"
        className="absolute inset-0 h-[115%] w-full object-cover object-center opacity-62 saturate-[0.85]"
        src="/assets/hero-night-city-loop.webm"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/assets/hero-night-city-poster.jpeg"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-[#0A0D14]/82 to-[#0A0D14]/25" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_22%,rgba(196,155,91,0.20),transparent_34%),radial-gradient(circle_at_18%_70%,rgba(255,255,255,0.08),transparent_28%)]" />

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl items-center px-5 py-20 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-3xl">
          <p data-testid="hero-overline" className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">
            Investigazioni private e aziendali
          </p>
          <h1 data-testid="hero-title" className="font-heading text-4xl font-light tracking-tighter text-white sm:text-5xl lg:text-7xl">
            La verità, con discrezione assoluta.
          </h1>
          <p data-testid="hero-subtitle" className="mt-7 max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg">
            Città Sicura affianca privati, professionisti e imprese con indagini mirate, prove documentabili e un metodo rigoroso. Una presenza locale a Legnago, aperta al pubblico e orientata alla tutela concreta dei tuoi interessi.
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Button
              asChild
              data-testid="hero-primary-cta"
              className="h-12 rounded-full bg-[#C49B5B] px-7 text-sm font-semibold text-[#0A0D14] shadow-[0_0_35px_rgba(196,155,91,0.28)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#D8B475] hover:shadow-[0_0_55px_rgba(196,155,91,0.38)]"
            >
              <a href="#contatti">Richiedi consulenza <ChevronRight className="h-4 w-4" /></a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-secondary-cta"
              className="h-12 rounded-full border-white/15 bg-white/5 px-7 text-sm font-semibold text-white backdrop-blur-sm transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#C49B5B]/50 hover:bg-white/10 hover:text-white"
            >
              <a href="#servizi">Esplora i servizi</a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function MethodSection() {
  const steps = [
    { icon: Search, title: "Ascolto riservato", text: "Analisi iniziale del caso, obiettivi, rischi e margini di intervento." },
    { icon: Crosshair, title: "Strategia d’indagine", text: "Piano operativo proporzionato, discreto e conforme alle normative vigenti." },
    { icon: BadgeCheck, title: "Report documentabile", text: "Consegna di elementi chiari, ordinati e utilizzabili nelle sedi opportune." },
  ];

  return (
    <section id="metodo" data-testid="method-section" className="relative overflow-hidden bg-[#0A0D14] py-24 sm:py-32">
      <div className="absolute left-0 top-1/4 h-72 w-72 rounded-full bg-[#C49B5B]/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-6 lg:grid-cols-12 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }} variants={fadeUp} className="lg:col-span-5">
          <p data-testid="method-overline" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">
            Metodo investigativo
          </p>
          <h2 data-testid="method-title" className="mt-4 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
            Lucidità operativa, presenza locale, massima riservatezza.
          </h2>
          <p data-testid="method-description" className="mt-6 text-base leading-relaxed text-zinc-400 md:text-lg">
            Ogni incarico viene gestito con equilibrio: ascoltiamo, definiamo una strategia, operiamo con discrezione e restituiamo un quadro chiaro. L’obiettivo è trasformare il dubbio in decisioni consapevoli.
          </p>
        </motion.div>

        <div className="grid gap-5 lg:col-span-7">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                data-testid={`method-step-${index + 1}`}
                className="group rounded-[2rem] border border-white/5 bg-[#111622]/85 p-6 transition-[border-color,transform,background-color] duration-300 hover:-translate-y-1 hover:border-[#C49B5B]/30 hover:bg-[#141A28]"
              >
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#C49B5B]/10 text-[#C49B5B]">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 data-testid={`method-step-${index + 1}-title`} className="font-heading text-xl font-medium text-white sm:text-2xl">
                      {step.title}
                    </h3>
                    <p data-testid={`method-step-${index + 1}-text`} className="mt-2 text-base leading-relaxed text-zinc-400">
                      {step.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section id="servizi" data-testid="services-section" className="bg-[#0A0D14] py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p data-testid="services-overline" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">
            Offerta di servizi
          </p>
          <h2 data-testid="services-title" className="mt-4 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
            Indagini costruite intorno alla realtà del cliente.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            data-testid="services-image-card-corporate"
            className="group relative min-h-[360px] overflow-hidden rounded-[2.25rem] border border-white/5 bg-[#111622] md:col-span-7"
          >
            <img src="https://images.pexels.com/photos/6814535/pexels-photo-6814535.jpeg" alt="Consulenza investigativa aziendale" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/45 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-9">
              <p data-testid="corporate-card-kicker" className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C49B5B]">Area business</p>
              <h3 data-testid="corporate-card-title" className="mt-3 font-heading text-2xl font-medium text-white sm:text-3xl">Tutela dell’impresa e del patrimonio informativo.</h3>
            </div>
          </motion.article>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            data-testid="services-stats-card"
            className="rounded-[2.25rem] border border-white/5 bg-[#111622] p-7 md:col-span-5 sm:p-9"
          >
            <Clock3 className="h-8 w-8 text-[#C49B5B]" strokeWidth={1.5} />
            <h3 data-testid="stats-card-title" className="mt-6 font-heading text-2xl font-medium text-white">Sempre orientati all’urgenza reale.</h3>
            <div className="mt-8 grid gap-5">
              {stats.map((stat) => (
                <div key={stat.label} data-testid={`stat-${stat.label.replaceAll(" ", "-")}`} className="flex items-end justify-between border-b border-white/10 pb-4">
                  <span className="font-heading text-4xl font-light text-white">{stat.value}</span>
                  <span className="text-right text-sm uppercase tracking-[0.16em] text-zinc-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.article
                key={service.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ delay: index * 0.08, duration: 0.62 }}
                data-testid={`service-card-${index + 1}`}
                className="group rounded-[2rem] border border-white/5 bg-[#111622] p-6 transition-[border-color,transform,background-color] duration-300 hover:-translate-y-1 hover:border-[#C49B5B]/30 hover:bg-[#151B29] md:col-span-6 lg:col-span-3"
              >
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#C49B5B]/10 text-[#C49B5B]">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 data-testid={`service-card-${index + 1}-title`} className="mt-6 font-heading text-xl font-medium text-white sm:text-2xl">
                  {service.title}
                </h3>
                <p data-testid={`service-card-${index + 1}-description`} className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">
                  {service.description}
                </p>
                <ul data-testid={`service-card-${index + 1}-features`} className="mt-6 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-zinc-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#C49B5B]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}

          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            data-testid="services-image-card-tech"
            className="group relative min-h-[320px] overflow-hidden rounded-[2.25rem] border border-white/5 bg-[#111622] md:col-span-12"
          >
            <img src="https://images.pexels.com/photos/5483248/pexels-photo-5483248.jpeg" alt="Analisi tecnologica e sicurezza digitale" className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-[#0A0D14]/75 to-[#0A0D14]/20" />
            <div className="relative z-10 max-w-2xl p-7 sm:p-10 lg:p-12">
              <p data-testid="tech-card-kicker" className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C49B5B]">Tecnica e prevenzione</p>
              <h3 data-testid="tech-card-title" className="mt-4 font-heading text-2xl font-medium text-white sm:text-4xl">Dalla bonifica ambientale alla videosorveglianza: sicurezza concreta, non improvvisata.</h3>
              <p data-testid="tech-card-description" className="mt-5 text-base leading-relaxed text-zinc-400 md:text-lg">Interveniamo con strumenti e procedure pensate per ridurre l’incertezza, proteggere luoghi sensibili e supportare decisioni rapide.</p>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="agenzia" data-testid="about-section" className="relative overflow-hidden bg-[#0D111B] py-24 sm:py-32">
      <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-[#C49B5B]/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-6">
          <p data-testid="about-overline" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">L’agenzia</p>
          <h2 data-testid="about-title" className="mt-4 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
            Esperienza al servizio della verità.
          </h2>
        </div>
        <div className="space-y-6 lg:col-span-6">
          <p data-testid="about-paragraph-1" className="text-base leading-relaxed text-zinc-400 md:text-lg">
            Città Sicura nasce per offrire un riferimento affidabile a chi ha bisogno di chiarezza: famiglie, aziende, studi professionali e privati cittadini. La nostra attività combina osservazione, analisi, tecnologia e conoscenza del territorio.
          </p>
          <p data-testid="about-paragraph-2" className="text-base leading-relaxed text-zinc-400 md:text-lg">
            Riceviamo su appuntamento presso la sede locale e curiamo ogni incarico con una comunicazione sobria, precisa e riservata. Prima di agire, definiamo obiettivi realistici e un perimetro operativo trasparente.
          </p>
          <div data-testid="about-highlight" className="rounded-[2rem] border border-[#C49B5B]/20 bg-[#C49B5B]/10 p-6 text-zinc-200">
            <Building2 className="mb-4 h-6 w-6 text-[#C49B5B]" strokeWidth={1.5} />
            <p className="text-base leading-relaxed">Una sede aperta al pubblico, un approccio consulenziale e una rete di competenze per affrontare casi delicati con metodo professionale.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [status, setStatus] = useState("");
  const [captchaIndex, setCaptchaIndex] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentCaptcha = captchaChallenges[captchaIndex];

  const refreshCaptcha = () => {
    setCaptchaIndex((value) => (value + 1) % captchaChallenges.length);
    setCaptchaAnswer("");
    setCaptchaError("");
    setSubmitError("");
    setStatus("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedAnswer = captchaAnswer.trim().toLowerCase();
    if (normalizedAnswer !== currentCaptcha.answer) {
      setStatus("");
      setSubmitError("");
      setCaptchaError("Verifica captcha non corretta. Controlla la risposta e riprova.");
      return;
    }

    setCaptchaError("");
    setSubmitError("");
    setStatus("");
    setIsSubmitting(true);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      nome: formData.get("nome"),
      cognome: formData.get("cognome"),
      telefono: formData.get("telefono"),
      email: formData.get("email"),
      richiesta: formData.get("richiesta"),
      privacy_accepted: formData.get("privacy") === "on",
      captcha_question: currentCaptcha.question,
      captcha_answer: captchaAnswer,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/api/contact`, payload);
      setStatus(response.data?.message || "Richiesta inviata correttamente.");
      toast("Richiesta inviata correttamente", {
        description: "Il messaggio è stato inoltrato all’agenzia.",
      });
      form.reset();
      refreshCaptcha();
      setStatus("Richiesta inviata correttamente. Ti ricontatteremo appena possibile.");
    } catch (error) {
      const message = error.response?.data?.detail || "Invio non riuscito. Riprova più tardi o contatta telefonicamente l’agenzia.";
      setSubmitError(message);
      toast("Invio non riuscito", { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="bg-[#0A0D14] py-24 sm:py-32 lg:py-40">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <p data-testid="contact-overline" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">Contatto riservato</p>
          <h2 data-testid="contact-title" className="mt-4 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
            Parla con un investigatore in modo discreto.
          </h2>
          <p data-testid="contact-description" className="mt-6 text-base leading-relaxed text-zinc-400 md:text-lg">
            Descrivi brevemente la situazione: ti ricontatteremo per valutare il caso, chiarire le possibilità operative e proporre il percorso più adatto.
          </p>

          <div className="mt-10 space-y-4">
            <a href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link" className="flex items-start gap-4 rounded-3xl border border-white/5 bg-[#111622] p-5 text-zinc-300 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#C49B5B]/30">
              <Phone className="mt-1 h-5 w-5 text-[#C49B5B]" strokeWidth={1.5} />
              <span><span className="block text-sm uppercase tracking-[0.18em] text-zinc-500">Telefono</span><span data-testid="contact-phone-text" className="mt-1 block text-white">{CONTACT.phone}</span></span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-start gap-4 rounded-3xl border border-white/5 bg-[#111622] p-5 text-zinc-300 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#C49B5B]/30">
              <Mail className="mt-1 h-5 w-5 text-[#C49B5B]" strokeWidth={1.5} />
              <span><span className="block text-sm uppercase tracking-[0.18em] text-zinc-500">Email</span><span data-testid="contact-email-text" className="mt-1 block text-white">{CONTACT.email}</span></span>
            </a>
            <div data-testid="contact-address-card" className="flex items-start gap-4 rounded-3xl border border-white/5 bg-[#111622] p-5 text-zinc-300">
              <MapPin className="mt-1 h-5 w-5 text-[#C49B5B]" strokeWidth={1.5} />
              <span><span className="block text-sm uppercase tracking-[0.18em] text-zinc-500">Sede</span><span data-testid="contact-address-text" className="mt-1 block text-white">{CONTACT.address}</span></span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={handleSubmit} className="rounded-[2.5rem] border border-white/10 bg-[#111622]/85 p-5 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="nome" data-testid="contact-name-label" className="text-zinc-300">Nome <span className="text-[#C49B5B]">*</span></Label>
                <Input id="nome" name="nome" data-testid="contact-name-input" required aria-required="true" placeholder="Il tuo nome" className="mt-2 h-12 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]" />
              </div>
              <div>
                <Label htmlFor="cognome" data-testid="contact-surname-label" className="text-zinc-300">Cognome <span className="text-[#C49B5B]">*</span></Label>
                <Input id="cognome" name="cognome" data-testid="contact-surname-input" required aria-required="true" placeholder="Il tuo cognome" className="mt-2 h-12 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="telefono" data-testid="contact-phone-label" className="text-zinc-300">Recapito Telefonico</Label>
                <Input id="telefono" name="telefono" type="tel" data-testid="contact-phone-input" required placeholder="Il tuo numero di telefono" className="mt-2 h-12 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email" data-testid="contact-email-label" className="text-zinc-300">Email <span className="text-[#C49B5B]">*</span></Label>
                <Input id="email" name="email" type="email" data-testid="contact-email-input" required aria-required="true" placeholder="nome@email.it" className="mt-2 h-12 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="richiesta" data-testid="contact-request-label" className="text-zinc-300">Richiesta</Label>
                <Textarea id="richiesta" name="richiesta" data-testid="contact-request-textarea" required placeholder="Descrivi brevemente la situazione..." className="mt-2 min-h-36 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]" />
              </div>
            </div>
            <div data-testid="contact-captcha-panel" className="mt-6 rounded-3xl border border-[#C49B5B]/20 bg-[#C49B5B]/[0.07] p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <Label htmlFor="captcha" data-testid="contact-captcha-label" className="text-zinc-300">Captcha Custom</Label>
                  <p data-testid="contact-captcha-question" className="mt-2 rounded-2xl border border-white/10 bg-[#0A0D14]/80 px-4 py-3 font-heading text-lg text-[#D8B475]">
                    {currentCaptcha.question}
                  </p>
                </div>
                <div className="flex-1">
                  <Input
                    id="captcha"
                    name="captcha"
                    data-testid="contact-captcha-input"
                    required
                    value={captchaAnswer}
                    onChange={(event) => {
                      setCaptchaAnswer(event.target.value);
                      setCaptchaError("");
                      setSubmitError("");
                    }}
                    placeholder="Risposta"
                    className="h-12 rounded-2xl border-white/10 bg-[#0A0D14] text-white placeholder:text-zinc-600 focus-visible:ring-[#C49B5B]"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="contact-captcha-refresh-button"
                  onClick={refreshCaptcha}
                  className="h-12 rounded-full border-white/10 bg-white/5 px-5 text-white transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#C49B5B]/40 hover:bg-white/10 hover:text-white"
                >
                  <RotateCcw className="h-4 w-4" /> Cambia
                </Button>
              </div>
              {captchaError && (
                <p data-testid="contact-captcha-error" className="mt-3 rounded-2xl border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-200">
                  {captchaError}
                </p>
              )}
            </div>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/5 bg-white/[0.03] p-4">
              <input
                id="privacy"
                name="privacy"
                type="checkbox"
                data-testid="contact-privacy-checkbox"
                required
                aria-required="true"
                className="mt-1 h-4 w-4 shrink-0 rounded border border-[#C49B5B]/60 bg-[#0A0D14] accent-[#C49B5B] outline-none ring-offset-0 transition-transform duration-200 checked:scale-105 focus-visible:ring-2 focus-visible:ring-[#C49B5B]"
              />
              <div className="text-sm leading-relaxed text-zinc-400">
                <Label htmlFor="privacy" data-testid="contact-privacy-label" className="cursor-pointer text-sm leading-relaxed text-zinc-400">
                  Autorizzo il trattamento dei dati personali per essere ricontattato in merito alla richiesta inviata. <span className="text-[#C49B5B]">*</span>
                </Label>{" "}
                <a
                  href="/privacy-policy"
                  data-testid="contact-privacy-policy-link"
                  className="text-[#D8B475] underline decoration-[#C49B5B]/40 underline-offset-4 transition-colors hover:text-white"
                >
                  Leggi Privacy Policy
                </a>
              </div>
            </div>
            {status && <p data-testid="contact-form-status" className="mt-4 rounded-2xl border border-[#C49B5B]/25 bg-[#C49B5B]/10 p-4 text-sm text-[#D8B475]">{status}</p>}
            {submitError && <p data-testid="contact-submit-error" className="mt-4 rounded-2xl border border-red-500/25 bg-red-500/10 p-4 text-sm text-red-200">{submitError}</p>}
            <Button type="submit" disabled={isSubmitting} data-testid="contact-submit-button" className="mt-6 h-12 w-full rounded-full bg-[#C49B5B] text-[#0A0D14] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#D8B475] hover:shadow-[0_0_45px_rgba(196,155,91,0.25)] disabled:cursor-not-allowed disabled:opacity-70">
              {isSubmitting ? "Invio in corso..." : "Invia la richiesta"} <ChevronRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function MapSection() {
  return (
    <section data-testid="map-section" className="bg-[#0A0D14] pb-24 sm:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2.5rem] border border-white/10 bg-[#111622]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="p-7 sm:p-10 lg:col-span-4 lg:p-12">
              <p data-testid="map-overline" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">Sede locale</p>
              <h2 data-testid="map-title" className="mt-4 font-heading text-3xl font-medium text-white sm:text-4xl">Riceviamo a Legnago.</h2>
              <p data-testid="map-description" className="mt-5 text-base leading-relaxed text-zinc-400">Un punto di riferimento accessibile per consulenze riservate e appuntamenti su casi privati o aziendali.</p>
              <p data-testid="map-address" className="mt-8 rounded-2xl border border-white/5 bg-[#0A0D14] p-5 text-white">{CONTACT.address}</p>
            </div>
            <div className="min-h-[420px] lg:col-span-8">
              <iframe
                data-testid="google-map-iframe"
                title="Mappa Città Sicura Legnago"
                src="https://www.google.com/maps?q=Via%20Papa%20Pio%20X%2C%2021%2C%2037045%20Legnago%20VR&output=embed"
                className="h-full min-h-[420px] w-full border-0 grayscale invert-[0.9] contrast-[0.8] hue-rotate-180"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LegalPage({ page }) {
  const content = legalPages[page];

  return (
    <div data-testid={`${page}-legal-page`} className="min-h-screen overflow-x-hidden bg-[#0A0D14] text-white">
      <Header />
      <main className="relative pt-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_18%,rgba(196,155,91,0.14),transparent_28%),radial-gradient(circle_at_85%_8%,rgba(255,255,255,0.06),transparent_25%)]" />
        <section className="relative mx-auto max-w-5xl px-5 py-20 sm:px-6 sm:py-28 lg:px-8">
          <a
            href="/"
            data-testid={`${page}-back-home-link`}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-zinc-300 transition-[border-color,color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#C49B5B]/40 hover:bg-white/10 hover:text-[#D8B475]"
          >
            ← Torna al sito
          </a>

          <div className="mt-10 rounded-[2.5rem] border border-white/10 bg-[#111622]/85 p-6 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-10 lg:p-12">
            <p data-testid={`${page}-legal-eyebrow`} className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C49B5B] sm:text-sm">
              {content.eyebrow}
            </p>
            <h1 data-testid={`${page}-legal-title`} className="mt-4 font-heading text-4xl font-light tracking-tighter text-white sm:text-5xl lg:text-6xl">
              {content.title}
            </h1>
            <p data-testid={`${page}-legal-subtitle`} className="mt-6 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-lg">
              {content.subtitle}
            </p>
            <p data-testid={`${page}-legal-updated`} className="mt-5 text-sm text-zinc-500">
              {content.updated}
            </p>

            <div className="mt-12 space-y-8">
              {content.sections.map((section, index) => (
                <article
                  key={section.title}
                  data-testid={`${page}-legal-section-${index + 1}`}
                  className="rounded-[1.75rem] border border-white/5 bg-[#0A0D14]/70 p-5 sm:p-7"
                >
                  <h2 data-testid={`${page}-legal-section-${index + 1}-title`} className="font-heading text-2xl font-medium text-white">
                    {section.title}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.body.map((paragraph) => (
                      <p key={paragraph} className="text-base leading-relaxed text-zinc-400">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/5 bg-[#070A10] py-10">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 text-sm text-zinc-500 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <p data-testid="footer-brand">© Città Sicura — Investigazioni Private e Aziendali</p>
        <div data-testid="footer-contact-and-legal" className="flex flex-wrap gap-x-4 gap-y-2 md:justify-end">
          <p data-testid="footer-contact">{CONTACT.email} · {CONTACT.phone}</p>
          <nav data-testid="footer-legal-navigation" className="flex flex-wrap gap-x-4 gap-y-2 text-zinc-400">
            <a data-testid="footer-privacy-link" href="/privacy-policy" className="transition-colors hover:text-[#D8B475]">Privacy Policy</a>
            <a data-testid="footer-cookie-link" href="/cookie-policy" className="transition-colors hover:text-[#D8B475]">Cookie Policy</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function Home() {
  return (
    <div data-testid="onepage-site" className="min-h-screen overflow-x-hidden bg-[#0A0D14] text-white">
      <Header />
      <main>
        <Hero />
        <MethodSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
        <MapSection />
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function App() {
  useEffect(() => {
    document.title = SITE_TITLE;
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<LegalPage page="privacy" />} />
        <Route path="/cookie-policy" element={<LegalPage page="cookies" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
